/**
 * KeepRulesControl - Shared keep-rule steppers for the probability tools
 *
 * Owns the three thresholds that decide whether an opening hand is keepable.
 * Labels spell out the asymmetry between the source floor (lands + rocks) and
 * the flood ceiling (raw lands only).
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';

import type { KeepRules } from '@/features/decks/lib/handSim';
import {
  MAX_LANDS_RANGE,
  MIN_SOURCES_RANGE,
  SLOW_START_RANGE,
  defaultKeepRules,
  isFormatDefault,
} from '@/features/decks/state/handSimSettings';
import type { DeckFormat } from '@/types/deck';

import SettingStepper from './SettingStepper';

// ============================================================================
// Types
// ============================================================================

export interface KeepRulesControlProps {
  rules: KeepRules;
  format: DeckFormat | null;
  onChange: (rules: KeepRules) => void;
  onReset: () => void;
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

export function keepRulesSummary(rules: KeepRules): string {
  return `${rules.minSources}+ sources · max ${rules.maxLands} lands · play by T${rules.slowStartTurn}`;
}

export default function KeepRulesControl({ rules, format, onChange, onReset, className }: KeepRulesControlProps): ReactElement {
  const atDefaults = isFormatDefault(rules, format);
  const defaults = defaultKeepRules(format);

  // Raising the floor above the ceiling lifts the ceiling instead of silently
  // rewriting what the user just asked for.
  const setMinSources = (value: number): void => {
    onChange({ ...rules, minSources: value, maxLands: Math.max(rules.maxLands, value) });
  };

  const setMaxLands = (value: number): void => {
    onChange({ ...rules, maxLands: value, minSources: Math.min(rules.minSources, value) });
  };

  return (
    <div className={clsx('space-y-2', className)}>
      <SettingStepper
        label="Min mana sources"
        hint="lands + mana rock equivalents"
        value={rules.minSources}
        min={MIN_SOURCES_RANGE.min}
        max={MIN_SOURCES_RANGE.max}
        onChange={setMinSources}
      />
      <SettingStepper
        label="Max lands before flood"
        hint="raw lands only — rocks never flood you"
        value={rules.maxLands}
        min={MAX_LANDS_RANGE.min}
        max={MAX_LANDS_RANGE.max}
        onChange={setMaxLands}
      />
      <SettingStepper
        label="Wants a play by turn"
        hint="later first plays are flagged slow"
        value={rules.slowStartTurn}
        min={SLOW_START_RANGE.min}
        max={SLOW_START_RANGE.max}
        onChange={(value) => onChange({ ...rules, slowStartTurn: value })}
      />

      <div className="flex items-center justify-between pt-1">
        <span className="text-ash/70 text-[10px]">
          Format default: {defaults.minSources}/{defaults.maxLands}/T{defaults.slowStartTurn}
        </span>
        <button
          type="button"
          onClick={onReset}
          disabled={atDefaults}
          className="text-aether rounded text-[11px] transition-colors hover:underline focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none disabled:cursor-not-allowed disabled:text-zinc-600 disabled:no-underline"
        >
          Reset to format default
        </button>
      </div>
    </div>
  );
}
